// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file Popover.tsx
 * @input Uses React, usePopover hook
 * @output Exports Popover component for click-triggered popovers
 * @position Layer component; declarative wrapper around usePopover hook
 *
 * For hover-triggered overlays, use HoverCard instead.
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/Popover/Popover.test.tsx
 * - /packages/core/src/Popover/index.ts
 * - /apps/storybook/stories/Popover.stories.tsx
 * - /packages/cli/templates/blocks/components/Popover/ (showcase blocks)
 */
import React, { useCallback, useRef } from 'react';
import { useIsomorphicLayoutEffect } from "../hooks/useIsomorphicLayoutEffect.js";
import * as stylex from '@stylexjs/stylex';
import { mergeProps } from "../utils/index.js";
import { usePopover } from "./usePopover.js";
import { layerAnimations } from "../Layer/layerAnimations.stylex.js";
import "../theme/tokens.stylex.js";
import { spacingVars } from "../theme/tokens.stylex.js";
import { InteractiveRoleContext } from "../InteractiveRoleContext/InteractiveRoleContext.js";
import { themeProps } from "../utils/themeProps.js";

// =============================================================================
// Helpers
// =============================================================================
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const BUTTON_SELECTOR = 'button, [role="button"]';

/**
 * Find the trigger button inside a container element.
 * Looks for `<button>` or `[role="button"]` — either the element itself
 * or the first matching descendant.
 */
function findTriggerButton(el) {
  if (el.matches(BUTTON_SELECTOR)) {
    return el;
  }
  return el.querySelector(BUTTON_SELECTOR);
}

// =============================================================================
// Types
// =============================================================================

/**
 * Props passed to render-prop children for explicit trigger wiring.
 */

// =============================================================================
// Styles
// =============================================================================

const styles = {
  contentPadding: {
    kLKAdn: "x1vlblms",
    kGO01o: "xvmdzux",
    kZCmMZ: "x126nfab",
    kwRFfy: "x1t818jl",
    $$css: true
  },
  gap: {
    keoZOQ: "xcsaf9d",
    k1K539: "x14cgwvg",
    $$css: true
  },
  customWidth: width => [{
    kzqmXN: (typeof width === 'number' ? `${width}px` : width) != null ? "x5lhr3w" : typeof width === 'number' ? `${width}px` : width,
    $$css: true
  }, {
    "--x-width": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(typeof width === 'number' ? `${width}px` : width)
  }],
  matchTrigger: {
    k7Eaqz: "xrzjruh",
    $$css: true
  }
};

// =============================================================================
// Component
// =============================================================================

/**
 * A click-triggered popover for displaying interactive content anchored to a trigger.
 *
 * Implements the button + dialog ARIA pattern. The trigger must contain a
 * `<button>` or `[role="button"]` element — the popover finds it and applies
 * click/keydown handlers and ARIA attributes automatically.
 *
 * Uses an inline-flex wrapper as the CSS anchor for stable positioning
 * (immune to pressed-state transforms like `:active { scale(0.98) }`).
 *
 * Focus is trapped inside the popover when open.
 * Supports light dismiss by default (click outside or Escape to close).
 *
 * For hover-triggered overlays, use {@link HoverCard} instead.
 *
 * @example
 * ```
 * <Popover label="Settings" content={<SettingsPanel />} placement="below">
 *   <Button label="Settings" />
 * </Popover>
 * <Popover
 *   isOpen={isOpen}
 *   onOpenChange={setIsOpen}
 *   label="Filter"
 *   content={<FilterForm />}>
 *   <Button label="Filter" />
 * </Popover>
 * <Popover
 *   anchorRef={myButtonRef}
 *   label="Actions"
 *   content={<ActionMenu />}
 *   placement="below"
 * />
 * ```
 */
export function Popover({
  children,
  anchorRef,
  content,
  placement = 'below',
  alignment = 'start',
  isOpen,
  onOpenChange,
  isEnabled = true,
  width,
  label,
  hasCloseButton,
  closeButtonLabel,
  hasAutoFocus,
  hasLightDismiss = true,
  hasEscapeDismiss = true,
  xstyle,
  className,
  style,
  'data-testid': testId
}) {
  const wrapperRef = useRef(null);
  const isControlled = isOpen !== undefined;
  // Track when the popover was last hidden by light dismiss to prevent
  // the trigger click from immediately re-opening it.
  const lastHideTimeRef = useRef(0);
  const handlePopoverShow = useCallback(() => {
    onOpenChange?.(true);
  }, [onOpenChange]);
  const handlePopoverHide = useCallback(() => {
    lastHideTimeRef.current = Date.now();
    onOpenChange?.(false);
  }, [onOpenChange]);
  const popover = usePopover({
    dialogLabel: label,
    hasLightDismiss,
    hasEscapeDismiss,
    hasCloseButton,
    closeButtonLabel,
    hasAutoFocus,
    onShow: handlePopoverShow,
    onHide: handlePopoverHide
  });

  // Shared handler for click events on the trigger button.
  const handleTriggerClick = useCallback(() => {
    if (!isEnabled) {
      return;
    }
    // If the popover was just closed by light dismiss (clicking outside),
    // the trigger click fires in the same event — skip re-opening.
    if (Date.now() - lastHideTimeRef.current < 50) {
      return;
    }
    popover.toggle();
  }, [isEnabled, popover]);

  // Shared handler for keydown events on role="button" elements.
  // Native <button> synthesizes click on Enter/Space, but role="button"
  // does not — we need to handle it explicitly.
  const handleTriggerKeyDown = useCallback(e => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleTriggerClick();
    }
  }, [handleTriggerClick]);

  /**
   * Attach click/keydown handlers and ARIA attributes to a trigger button.
   * Used by both sibling mode and children mode.
   */
  const attachTrigger = useCallback(button => {
    // ARIA attributes
    button.setAttribute('aria-haspopup', popover.triggerProps['aria-haspopup']);
    button.setAttribute('aria-expanded', String(popover.triggerProps['aria-expanded']));
    button.setAttribute('aria-controls', popover.triggerProps['aria-controls']);

    // Event handlers
    button.addEventListener('click', handleTriggerClick);
    // Only add keydown for role="button" — native <button> already
    // synthesizes click events for Enter/Space.
    const needsKeyDown = button.tagName !== 'BUTTON' && button.getAttribute('role') === 'button';
    if (needsKeyDown) {
      button.addEventListener('keydown', handleTriggerKeyDown);
    }
    return () => {
      button.removeAttribute('aria-haspopup');
      button.removeAttribute('aria-expanded');
      button.removeAttribute('aria-controls');
      button.removeEventListener('click', handleTriggerClick);
      if (needsKeyDown) {
        button.removeEventListener('keydown', handleTriggerKeyDown);
      }
    };
  }, [popover, handleTriggerClick, handleTriggerKeyDown]);

  // Sibling mode: attach to external anchorRef
  useIsomorphicLayoutEffect(() => {
    if (!anchorRef) {
      return;
    }
    const el = anchorRef.current;
    if (!el) {
      return;
    }
    const button = findTriggerButton(el);
    if (!button) {
      console.warn('Popover: anchorRef must reference a <button> or [role="button"] element. ' + 'The popover trigger implements the button + dialog ARIA pattern.');
    }
    if (!button) {
      return;
    }

    // Set up anchor positioning on the anchorRef element itself
    popover.triggerRef(el);

    // Attach handlers + ARIA to the button
    const detach = attachTrigger(button);
    return () => {
      popover.triggerRef(null);
      detach();
    };
  }, [anchorRef, popover, attachTrigger]);

  // Children mode: use wrapper as CSS anchor, find button inside for
  // ARIA + event handlers.
  useIsomorphicLayoutEffect(() => {
    if (anchorRef) {
      return;
    } // Skip if using anchorRef mode
    if (typeof children === 'function') {
      return;
    } // Skip if using render prop mode

    const wrapper = wrapperRef.current;
    if (!wrapper) {
      return;
    }

    // Use the wrapper as the CSS anchor — it doesn't receive pressed-state
    // transforms, so the anchor position stays stable.
    popover.triggerRef(wrapper);

    // Find the button inside the wrapper
    const button = findTriggerButton(wrapper);
    if (!button) {
      console.warn('Popover: children must contain a <button> or [role="button"] element. ' + 'The popover trigger implements the button + dialog ARIA pattern.');
    }
    if (!button) {
      return;
    }
    const detach = attachTrigger(button);
    return () => {
      popover.triggerRef(null);
      detach();
    };
  }, [anchorRef, popover, attachTrigger]);

  // Sync controlled state
  useIsomorphicLayoutEffect(() => {
    if (!isControlled) {
      return;
    }
    if (isOpen && !popover.isOpen) {
      popover.show();
    } else if (!isOpen && popover.isOpen) {
      popover.hide();
    }
  }, [isOpen, isControlled, popover]);

  // Determine popover xstyle
  const popoverXstyle = width ? styles.customWidth(width) : styles.matchTrigger;

  // Sibling mode: render only the popover (no wrapper needed)
  if (anchorRef && children == null) {
    return /*#__PURE__*/_jsx(_Fragment, {
      children: popover.render(/*#__PURE__*/_jsx("div", {
        "data-testid": testId,
        ...mergeProps(themeProps('popover'), stylex.props(styles.contentPadding, xstyle), className, style),
        children: content
      }), {
        placement,
        alignment,
        xstyle: [popoverXstyle, styles.gap, layerAnimations[placement]]
      })
    });
  }

  // Render prop mode: children is a function — pass trigger props directly
  const isRenderProp = typeof children === 'function';
  if (isRenderProp) {
    const triggerProps = {
      ref: popover.triggerRef,
      onClick: handleTriggerClick,
      'aria-haspopup': 'dialog',
      'aria-expanded': popover.isOpen,
      'aria-controls': popover.id
    };
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [children(triggerProps), popover.render(/*#__PURE__*/_jsx("div", {
        "data-testid": testId,
        ...mergeProps(themeProps('popover'), stylex.props(styles.contentPadding, xstyle), className, style),
        children: content
      }), {
        placement,
        alignment,
        xstyle: [popoverXstyle, styles.gap, layerAnimations[placement]]
      })]
    });
  }

  // Automatic mode: wrap children in context + anchor wrapper
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(InteractiveRoleContext, {
      value: "button",
      children: /*#__PURE__*/_jsx("div", {
        ref: wrapperRef,
        ...{
          className: "x3nfvp2"
        },
        children: children
      })
    }), popover.render(/*#__PURE__*/_jsx("div", {
      "data-testid": testId,
      ...mergeProps(themeProps('popover'), stylex.props(styles.contentPadding, xstyle), className, style),
      children: content
    }), {
      placement,
      alignment,
      xstyle: [popoverXstyle, styles.gap, layerAnimations[placement]]
    })]
  });
}
Popover.displayName = 'Popover';